import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Car, 
  Wrench, 
  DollarSign, 
  Network,
  ArrowRight,
  CheckCircle2,
  FileJson,
  RefreshCw,
  FileText
} from 'lucide-react';

const useCases = [
  {
    id: 'dms-integration',
    icon: Building2,
    title: 'DMS Integration',
    subtitle: 'Dealer Management Systems',
    description: 'Connect dealer management systems seamlessly with standardized data formats for customers, inventory, and transactions.',
    benefits: [
      'Unified customer records across all systems',
      'Real-time inventory synchronization',
      'Consistent deal and transaction data',
      'Simplified multi-location operations'
    ],
    standards: ['Customer', 'Vehicle', 'Deal', 'Inventory']
  },
  {
    id: 'crm-sync',
    icon: Users,
    title: 'CRM Synchronization',
    subtitle: 'Customer Relationship Management',
    description: 'Keep customer data consistent across CRM platforms with standardized contact and lead formats.',
    benefits: [
      'No more duplicate customer records',
      'Complete interaction history transfers',
      'Standardized lead scoring data',
      'Cross-platform campaign tracking'
    ],
    standards: ['Customer', 'Lead', 'Contact', 'Communication']
  },
  {
    id: 'adf-lead-management',
    icon: FileText,
    title: 'ADF Lead Management',
    subtitle: 'Auto-lead Data Format',
    description: 'Manage, validate, and build ADF lead templates with modern tooling. Continue using ADF today with a clear path to API-based standards when you\'re ready.',
    benefits: [
      'Validate ADF XML against the official specification',
      'Build ADF templates with a visual editor',
      'Export clean, compliant lead data',
      'Migrate to API standards at your own pace'
    ],
    standards: ['ADF', 'Lead', 'Customer', 'Vehicle']
  },
  {
    id: 'inventory-management',
    icon: Car,
    title: 'Inventory Management',
    subtitle: 'Vehicle & Parts Tracking',
    description: 'Standardized vehicle and parts data that works across inventory systems, marketplaces, and websites.',
    benefits: [
      'Accurate vehicle specifications',
      'Consistent pricing across channels',
      'Automated marketplace listings',
      'Real-time availability updates'
    ],
    standards: ['Vehicle', 'VehicleInventory', 'Part', 'PartInventory']
  },
  {
    id: 'service-operations',
    icon: Wrench,
    title: 'Service Operations',
    subtitle: 'Fixed Operations & Repair',
    description: 'Consistent repair order and appointment formats that connect service departments with customers and vendors.',
    benefits: [
      'Standardized repair order formats',
      'Consistent labor and parts pricing',
      'Automated appointment scheduling',
      'Complete service history tracking'
    ],
    standards: ['RepairOrder', 'Appointment', 'ServiceHistory', 'TechnicianTime']
  },
  {
    id: 'financial-reporting',
    icon: DollarSign,
    title: 'Financial Reporting',
    subtitle: 'Transactions & Accounting',
    description: 'Transaction data that works everywhere—from sales reports to accounting systems to manufacturer portals.',
    benefits: [
      'Accurate commission calculations',
      'Consistent deal structure data',
      'Automated financial reporting',
      'Manufacturer compliance ready'
    ],
    standards: ['Deal', 'Transaction', 'Commission', 'Payment']
  },
  {
    id: 'multi-vendor',
    icon: Network,
    title: 'Multi-Vendor Ecosystems',
    subtitle: 'Interoperability & Integration',
    description: 'Enable true interoperability between any vendors in the automotive ecosystem without custom development.',
    benefits: [
      'Plug-and-play vendor switching',
      'Reduced integration timelines',
      'Lower development costs',
      'Future-proof architecture'
    ],
    standards: ['All Standards']
  }
];

export default function UseCases() {
  return (
    <Layout>
      <div className="container py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">Use Cases</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            What Can You Build?
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From API standards to ADF lead management—see how organizations use 
            AutomotiveAPI tools to streamline data integration.
          </p>
        </div>

        {/* How It Works */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <FileJson className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">1. Choose or Build</h3>
                <p className="text-muted-foreground text-sm">
                  Browse standards or use our builders to create custom 
                  API schemas and ADF templates.
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">2. Validate Data</h3>
                <p className="text-muted-foreground text-sm">
                  Use our validator to ensure your data matches 
                  the schema before sending or receiving.
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <RefreshCw className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">3. Integrate</h3>
                <p className="text-muted-foreground text-sm">
                  Exchange data with any partner using the same 
                  standards. No custom mapping required.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Use Cases Grid */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Industry Applications</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase) => (
              <Card key={useCase.id} className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <useCase.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{useCase.title}</CardTitle>
                      <p className="text-sm text-muted-foreground">{useCase.subtitle}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">{useCase.description}</p>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Key Benefits</h4>
                    <ul className="space-y-1">
                      {useCase.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span className="text-muted-foreground">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Related Standards</h4>
                    <div className="flex flex-wrap gap-2">
                      {useCase.standards.map((standard) => (
                        <Badge key={standard} variant="secondary">{standard}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Example Integration */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Example: DMS to CRM Sync</h2>
          <Card>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="text-center">
                  <div className="p-4 rounded-lg bg-muted inline-block mb-3">
                    <Building2 className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold">DMS System</h3>
                  <p className="text-sm text-muted-foreground">Exports customer data</p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-lg bg-primary/10 inline-block mb-3">
                    <FileJson className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="font-semibold text-primary">AutomotiveAPI Standard</h3>
                  <p className="text-sm text-muted-foreground">Customer schema v1.0.0</p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-lg bg-muted inline-block mb-3">
                    <Users className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold">CRM System</h3>
                  <p className="text-sm text-muted-foreground">Imports customer data</p>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  <strong>Result:</strong> Both systems speak the same language. 
                  Customer data flows seamlessly with no custom mapping, no data loss, 
                  and full validation at each step.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Card className="bg-muted/50">
            <CardContent className="py-12">
              <h2 className="text-2xl font-bold mb-4">Ready to Standardize?</h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Browse API standards, build custom schemas, or manage your ADF leads 
                with modern tooling.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg">
                  <Link to="/api-standards">
                    Explore Standards
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/adf-standard/builder">
                    ADF Builder
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/docs">
                    Documentation
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
